import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import { useAppSelector } from 'hooks/reduxHooks'
import { getBaseTierOfferWithJoiningFee } from 'selectors/luxPlusSelectors'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LuxPlusRetentionModalFooter from './LuxPlusRetentionModalFooter'

interface Props {
  item: App.MemberSubscriptionItem
  onContinueToCancel: () => void
  onBack: () => void
}

const PricingCard = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.L};
  padding: ${rem(20)};
  width: 100%;
`

// Stack the two prices on mobile (horizontal divider between them); side-by-side on tablet+.
const PriceColumns = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: stretch;
  }
`

const PriceColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(12)};
  text-align: center;
`

const PriceDivider = styled.span`
  flex-shrink: 0;
  align-self: stretch;
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.neutral.surface.lightForeground};

  ${mediaQueryUp.tablet} {
    width: 1px;
    height: auto;
  }
`

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${rem(8)};
`

const PriceCaption = styled(CaptionTextBlock)`
  text-transform: uppercase;
  letter-spacing: ${rem(0.7)};
`

const messages = defineMessages({
  title: {
    id: 'zzoBJzBTho3o4LLyH1H7',
    defaultMessage: 'Keep your current member price',
    description: 'Title of the LuxPlus+ keep-your-price retention modal',
  },
  body: {
    id: '7KvWMfd8wkV_-kuvjuve',
    defaultMessage: "New members now pay {newMemberPrice}/year. With auto-renewal on, your annual rate of {renewalPrice}/year is locked in, so you'll keep enjoying the same benefits for less. If you turn it off and your membership lapses, you'll need to rejoin for {newMemberPriceRejoin}/year.",
    description: 'Explanatory paragraph in the keep-your-price retention modal',
  },
  renewalPriceLabel: {
    id: 'jWuxPiDD2O6CgWe29tJn',
    defaultMessage: 'Your renewal price',
    description: "Label above the member's locked-in renewal price in the keep-your-price retention modal",
  },
  newMemberPriceLabel: {
    id: 'x2PF5m39q0CqqVjs9slH',
    defaultMessage: 'New member price',
    description: 'Label above the current new-member price in the keep-your-price retention modal',
  },
  perYear: {
    id: '5KtxOFbSlKB6HEOCKoxD',
    defaultMessage: '/year',
    description: 'Per-year suffix shown after a membership price in the keep-your-price retention modal',
  },
})

function LuxPlusManageAccountModalKeepPrice({ item, onContinueToCancel, onBack }: Props) {
  const intl = useIntl()

  const renewalPrice = item.currentRenewalOffer?.price ?? 0
  const newMemberPrice = useAppSelector(getBaseTierOfferWithJoiningFee)?.price ?? 0

  return <>
    <ModalHeader title={intl.formatMessage(messages.title)} onBackButtonClick={onBack} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={20}>
          <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              {...messages.body}
              values={{
                newMemberPrice: <FormatCurrency value={newMemberPrice} />,
                newMemberPriceRejoin: <FormatCurrency value={newMemberPrice} />,
                renewalPrice: <FormatCurrency value={renewalPrice} />,
              }}
            />
          </BodyText>
          <PricingCard>
            <PriceColumns>
              <PriceColumn>
                <PriceCaption variant="small" weight="bold" colour="light-foreground-alt">
                  {intl.formatMessage(messages.renewalPriceLabel)}
                </PriceCaption>
                <PriceRow>
                  <HeadingTextBlock variant="heading2" colour="lux-plus">
                    <FormatCurrency value={renewalPrice} />
                  </HeadingTextBlock>
                  <BodyText variant="medium" colour="lux-plus">{intl.formatMessage(messages.perYear)}</BodyText>
                </PriceRow>
              </PriceColumn>
              <PriceDivider />
              <PriceColumn>
                <PriceCaption variant="small" weight="bold" colour="light-foreground-alt">
                  {intl.formatMessage(messages.newMemberPriceLabel)}
                </PriceCaption>
                <PriceRow>
                  <HeadingTextBlock variant="heading2">
                    <FormatCurrency value={newMemberPrice} />
                  </HeadingTextBlock>
                  <BodyText variant="medium" colour="light-foreground-alt">{intl.formatMessage(messages.perYear)}</BodyText>
                </PriceRow>
              </PriceColumn>
            </PriceColumns>
          </PricingCard>
        </Group>
      </ModalContent>
    </ModalBody>
    <LuxPlusRetentionModalFooter onContinueToCancel={onContinueToCancel} />
  </>
}

export default LuxPlusManageAccountModalKeepPrice
